import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  auditPublicWhatsappHandoff,
  getPublicPaymentRequest,
  startPublicPayment,
  updatePublicPaymentSelection,
} from '../api/client';
import { sortInvoicesOldestFirst } from '../lib/openBalanceDebtScope';

interface PublicInvoice {
  invoice_id: string;
  document_number: string;
  issue_date?: string;
  open_amount: number;
  currency: string;
  description?: string;
  status: string;
}

interface PublicRequest {
  payment_request_id: string;
  business: { id: string; name: string };
  customer: { id: string; display_name: string };
  currency: string;
  status: string;
  expires_at: string;
  invoices: PublicInvoice[];
  selected_invoice_ids: string[];
  total_amount: number;
  whatsapp_handoff_url?: string | null;
}

function formatAmount(amount: number, currency: string) {
  return amount.toLocaleString('he-IL', { style: 'currency', currency: currency || 'ILS' });
}

export default function PublicPaymentRequest() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [request, setRequest] = useState<PublicRequest | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPublicPaymentRequest(token)
      .then((res) => {
        setRequest(res.data);
        setSelected(res.data.selected_invoice_ids || []);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || t('publicPaymentRequest.unavailable'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  const selectedTotal = useMemo(() => {
    if (!request) return 0;
    const byId = new Map(request.invoices.map((invoice) => [invoice.invoice_id, invoice]));
    return selected.reduce((sum, id) => sum + (byId.get(id)?.open_amount || 0), 0);
  }, [request, selected]);

  const sortedInvoices = useMemo(
    () => sortInvoicesOldestFirst(request?.invoices || [], (invoice) => invoice.issue_date),
    [request],
  );

  const toggleInvoice = async (invoiceId: string) => {
    const next = selected.includes(invoiceId)
      ? selected.filter((id) => id !== invoiceId)
      : [...selected, invoiceId];
    setSelected(next);
    try {
      await updatePublicPaymentSelection(token, next);
    } catch {
      setSelected(selected);
    }
  };

  const pay = async (mode: 'selected' | 'all') => {
    setBusy(true);
    setError(null);
    try {
      const res = await startPublicPayment(token, { mode, selected_invoice_ids: selected });
      if (res.data.payment_url) {
        window.location.href = res.data.payment_url;
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('publicPaymentRequest.startPaymentError'));
    } finally {
      setBusy(false);
    }
  };

  const continueWhatsapp = async () => {
    if (!request?.whatsapp_handoff_url) return;
    await auditPublicWhatsappHandoff(token).catch(() => undefined);
    window.location.href = request.whatsapp_handoff_url;
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-600">{t('publicPaymentRequest.loading')}</div>;
  }

  if (error || !request) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">{t('publicPaymentRequest.unavailableTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600">{error || t('publicPaymentRequest.unavailable')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <main className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="border-b border-gray-100 pb-4">
          <h1 className="text-xl font-semibold text-gray-900">{request.business.name}</h1>
          <p className="mt-1 text-sm text-gray-600">{request.customer.display_name || request.customer.id}</p>
        </div>

        <div className="mt-5 space-y-3">
          {sortedInvoices.map((invoice) => {
            const checked = selected.includes(invoice.invoice_id);
            return (
              <label key={invoice.invoice_id} className={`flex cursor-pointer items-center justify-between gap-4 rounded-lg border p-4 ${checked ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={invoice.status !== 'open'}
                    onChange={() => toggleInvoice(invoice.invoice_id)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{t('publicPaymentRequest.invoiceNumber', { number: invoice.document_number })}</div>
                    {invoice.description && <div className="text-sm text-gray-500">{invoice.description}</div>}
                  </div>
                </div>
                <div className="text-sm font-semibold text-gray-900">{formatAmount(invoice.open_amount, invoice.currency)}</div>
              </label>
            );
          })}
        </div>

        <div className="mt-6 rounded-lg bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('publicPaymentRequest.selectedTotal')}</span>
            <span className="text-lg font-semibold text-gray-900">{formatAmount(selectedTotal, request.currency)}</span>
          </div>
        </div>

        {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {request.whatsapp_handoff_url && (
            <button type="button" onClick={continueWhatsapp} className="rounded-lg border border-green-600 px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-50">
              {t('publicPaymentRequest.continueWhatsApp')}
            </button>
          )}
          <button type="button" onClick={() => pay('all')} disabled={busy} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {t('publicPaymentRequest.payAll')}
          </button>
          <button type="button" onClick={() => pay('selected')} disabled={busy || selected.length === 0} className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50">
            {t('publicPaymentRequest.paySelected')}
          </button>
        </div>
      </main>
    </div>
  );
}
